// /home/coder/project/frontend/src/pages/tecnicaloffice/contracts/ToContractsDetail.tsx

import React, { useMemo } from 'react';
import { Spin, Alert, Typography, Divider, Row, message, Space, Descriptions, Tag, Col, Card } from 'antd';
import { RollbackOutlined, EditOutlined } from '@ant-design/icons';
import { TbContract } from 'react-icons/tb';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

// Import the specific interface for the view data
import { VwToContractDetails, CorCountries, CorCurrencies } from '../../../types';
import FileDownloadTool from '../../../components/common/FileDownloadTool';
// Import the API functions
import { fetchSqlData } from '../../../services/api';
// Import hooks for fetching related data (countries, currencies)
import { useGetCountries, useGetCurrencies } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const RTbContract: React.FC<any> = (props) =>
  React.createElement(TbContract as unknown as React.ComponentType<any>, props) as React.ReactElement;

// Helper to get color for status tag
const getStatusColor = (status: string | undefined | null): string => {
    const strStatus = String(status).toLowerCase();
    switch (strStatus) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
};

const ToContractsDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // Get ID from URL
  const contractId = parseInt(id || '0', 10);

  // --- Data Fetching ---
  // Fetch the specific contract details using the view
  const { data: contractDataArray, isLoading: isLoadingContract, error: fetchContractError } = useQuery<VwToContractDetails[], Error>({
    queryKey: ['vwContractDetails', contractId], // Unique key including the ID
    queryFn: () => fetchSqlData('vw_to_contract_details', 'id', contractId), // Fetch from view by ID
    enabled: !!contractId, // Only run query if contractId is valid
  });

  // Fetch countries and currencies for displaying names
  const { data: countriesResult, isLoading: isLoadingCountries } = useGetCountries();
  const { data: currenciesResult, isLoading: isLoadingCurrencies } = useGetCurrencies();

  const countries = useMemo(() => countriesResult?.data || [], [countriesResult]);
  const currencies = useMemo(() => currenciesResult?.data || [], [currenciesResult]);

  // Combine loading/error states
  const isLoading = isLoadingContract || isLoadingCountries || isLoadingCurrencies;
  const fetchError = fetchContractError; // Prioritize contract fetch error for display

  // --- Data Processing ---
  const contractData = useMemo(() => {
    return contractDataArray && contractDataArray.length > 0 ? contractDataArray[0] : null;
  }, [contractDataArray]);

  // Helper functions to get names from codes
  const getCountryName = (code: string | null | undefined): string => {
    if (!code || !countries.length) return code || '-';
    const country = countries.find(c => c.country_code === code);
    return country ? `${country.name} (${code})` : code;
  };

  const getCurrencyName = (code: string | null | undefined): string => {
    if (!code || !currencies.length) return code || '-';
    const currency = currencies.find(c => c.currency_code === code);
    return currency ? `${currency.name} (${code})` : code;
  };

  const formatValue = (value: string | null | undefined, currencyCode: string | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value; // Return original string if not a number
    return `${numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyCode || ''}`.trim();
  };

  // --- Loading and Error States ---
  if (isLoading) return <Card><Spin tip="Loading contract details..." /></Card>;
  if (fetchError) {
    message.error(`Error fetching contract details: ${fetchError.message}`);
    return (
      <Card>
        <Alert message="Error" description="Could not load contract details." type="error" showIcon />
        <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/contracts')} style={{ marginTop: 16 }}>Back to List</Button>
      </Card>
    );
  }
  if (!contractData) {
    return (
      <Card>
        <Alert message={`Contract with ID ${contractId} not found`} type="warning" showIcon />
        <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/contracts')} style={{ marginTop: 16 }}>Back to List</Button>
      </Card>
    );
  }

  // --- Render ---
  return (
    <Card>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <RTbContract size={20} style={{ marginRight: 8 }} /> Contract Detail: {contractData.contract_name} (ID: {contractData.id})
          </Title>
        </Col>
        <Col>
          <Space>
            <Button action="edit" icon={<EditOutlined />} onClick={() => navigate(`/to/contracts/edit/${contractId}`)}>Edit</Button>
            <Button icon={<RollbackOutlined />} onClick={() => navigate('/to/contracts')}>Back to List</Button>
          </Space>
        </Col>
      </Row>
      <Divider />

      {/* Descriptions */}
      <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} size="small">
        <Descriptions.Item label={<strong>Contract Name</strong>} span={1}>{contractData.contract_name || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Company</strong>} span={1}>{contractData.company_name || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Reference ID</strong>} span={1}>{contractData.reference_id || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Status</strong>} span={1}>
          <Tag color={getStatusColor(contractData.status)}>{contractData.status || '-'}</Tag>
        </Descriptions.Item>

        <Descriptions.Item label={<strong>Signing Place</strong>} span={1}>{getCountryName(contractData.signing_place)}</Descriptions.Item>
        <Descriptions.Item label={<strong>Signing Date</strong>} span={1}>{contractData.signing_date ? dayjs(contractData.signing_date).format('DD MMMM YYYY') : '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Delivery Place</strong>} span={1}>{getCountryName(contractData.delivery_place)}</Descriptions.Item>
        <Descriptions.Item label={<strong>Start Date</strong>} span={1}>{contractData.start_date ? dayjs(contractData.start_date).format('DD MMMM YYYY') : '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>End Date</strong>} span={1}>{contractData.end_date ? dayjs(contractData.end_date).format('DD MMMM YYYY') : '-'}</Descriptions.Item>

        <Descriptions.Item label={<strong>Contract Value 1</strong>} span={1}>{formatValue(contractData.contract_value1, contractData.contract_currency1)}</Descriptions.Item>
        <Descriptions.Item label={<strong>Contract Type</strong>} span={1}>{contractData.contract_type || '-'}</Descriptions.Item>
        <Descriptions.Item label={<strong>Contract Value 2</strong>} span={1}>{formatValue(contractData.contract_value2, contractData.contract_currency2)}</Descriptions.Item>

        <Descriptions.Item label={<strong>Payment Type</strong>} span={1}>{contractData.payment_type || '-'}</Descriptions.Item>

      </Descriptions>

      {/* Description Section */}
      <Divider />
      <Title level={5}>Description</Title>
      <div style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: '12px', borderRadius: '4px', minHeight: '80px' }}>{contractData.description || '-'}</div>

      {/* Attachments Section */}
      {contractData.upload_path && String(contractData.upload_path).trim() !== '' && (
        <>
          <Divider />
          <Title level={5}>Attachments</Title>
          <FileDownloadTool
            contentPath={contractData.upload_path}
            buttonText={
              <Button
                type="primary"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Attached Files
              </Button>
            }
          />
        </>
      )}
    </Card>
  );
};

export default ToContractsDetail;